import React from 'react';
import { CheckCircle2Icon, ChevronRightIcon, TargetIcon } from 'lucide-react';
import { ProgressBar } from '../ui/ProgressBar';
import { goals, type Goal } from '../../data/perfectDay';
interface GoalProgressProps {
  onOpenRecords: (recordSetId: string) => void;
}
type GoalStatus = 'complete' | 'on-pace' | 'behind' | 'at-risk';
interface GoalView {
  status: GoalStatus;
  statusLabel: string;
  progressLabel: string;
  percent: number;
  tone: 'success' | 'teal' | 'warn' | 'danger';
}
function describeGoal(goal: Goal): GoalView {
  if (goal.inverted) {
    const complete = goal.current <= goal.target;
    return {
      status: complete ? 'complete' : 'at-risk',
      statusLabel: complete ? 'Complete' : 'At risk',
      progressLabel: complete ? 'No overdue tasks' : `${goal.current} ${goal.unit}`,
      percent: complete ? 100 : 0,
      tone: complete ? 'success' : 'danger'
    };
  }
  const percent = Math.min(100, Math.round(goal.current / goal.target * 100));
  if (goal.current >= goal.target) {
    return {
      status: 'complete',
      statusLabel: 'Complete',
      progressLabel: `${goal.current} of ${goal.target} ${goal.unit}`,
      percent: 100,
      tone: 'success'
    };
  }
  const onPace = percent >= 55;
  return {
    status: onPace ? 'on-pace' : 'behind',
    statusLabel: onPace ? 'On pace' : 'Behind pace',
    progressLabel: `${goal.current} of ${goal.target} ${goal.unit}`,
    percent,
    tone: onPace ? 'teal' : 'warn'
  };
}
const statusBadge: Record<GoalStatus, string> = {
  complete: 'bg-success-50 text-success-700 ring-success-200',
  'on-pace': 'bg-teal-50 text-teal-700 ring-teal-200',
  behind: 'bg-warn-50 text-warn-700 ring-warn-200',
  'at-risk': 'bg-danger-50 text-danger-700 ring-danger-200'
};
export function GoalProgress({
  onOpenRecords
}: GoalProgressProps) {
  const completeCount = goals.filter(goal => describeGoal(goal).status === 'complete').length;
  return <section aria-labelledby="daily-goals-heading" className="rounded-2xl border border-navy-100 bg-white shadow-card" data-id="element-1" data-mp="1uskf5ztal7mi">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-navy-100 px-5 py-4" data-id="element-2" data-mp="3lwb3c5k51oi">
        <div data-id="element-3" data-mp="7nq6b4wzhdha">
          <h2 id="daily-goals-heading" className="text-lg font-semibold text-navy-900" data-id="element-4" data-mp="7n82nn7zrwzc">
            Daily Goal Progress
          </h2>
          <p className="mt-1 text-sm text-navy-500" data-id="element-5" data-mp="hmp6dz3oo0so">
            {completeCount} of {goals.length} goals met so far today
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 px-3 py-1.5 text-xs font-semibold text-navy-600 ring-1 ring-navy-200" data-id="element-6" data-mp="mjfmunjegyem">
          <TargetIcon className="h-4 w-4" aria-hidden="true" data-id="element-7" data-mp="ve3pe5xlc1zq" />
          Resets at midnight
        </span>
      </div>

      <ul className="divide-y divide-navy-100" data-id="element-8" data-mp="10dq30ghbzk92">
        {goals.map(goal => {
        const view = describeGoal(goal);
        return <li key={goal.id} data-id="element-9" data-mp="159f62tsk7ja4">
              <button type="button" onClick={() => onOpenRecords(goal.recordSetId)} aria-label={`${goal.label}: ${view.progressLabel}. Open contributing records.`} className="group w-full px-4 py-4 text-left transition-colors hover:bg-navy-50/50 sm:px-5" data-id="element-10" data-mp="1fg81xus8l0oc">
                <div className="flex items-start justify-between gap-3" data-id="element-11" data-mp="11hi2w9luwqxc">
                  <div className="min-w-0" data-id="element-12" data-mp="1y6g0dzkzr2y7">
                    <p className="flex items-center gap-1.5 text-sm font-semibold text-navy-900" data-id="element-13" data-mp="1y8akjbofur18">
                      {view.status === 'complete' ? <CheckCircle2Icon className="h-4 w-4 shrink-0 text-success-600" aria-hidden="true" data-id="element-14" data-mp="1oflfozne8bev" /> : null}
                      {goal.label}
                    </p>
                    <p className="mt-1 text-sm text-navy-600" data-id="element-15" data-mp="2c67l0j60jhl6">
                      {view.progressLabel}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2" data-id="element-16" data-mp="1wmd2l6higvfp">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ring-1 ${statusBadge[view.status]}`} data-id="element-17" data-mp="21naptxhdofo2">
                      {view.statusLabel}
                    </span>
                    <ChevronRightIcon className="h-4 w-4 text-navy-300 group-hover:text-navy-600" aria-hidden="true" data-id="element-18" data-mp="2fdxhx6smv7b6" />
                  </div>
                </div>
                <ProgressBar className="mt-3" size="sm" tone={view.tone} value={view.percent} label={`${goal.label} progress`} data-id="element-19" data-mp="26lccsthxcz4t" />
              </button>
            </li>;
      })}
      </ul>
    </section>;
}